import React from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation, PapiExtendedDeviation, PapiDeviationTier } from '@wix/da-papi-types';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { useIsCurrentUserDeviationAuthor } from '../../utils/hooks/useIsCurrentUserDeviationAuthor';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import ArtStage from '../../ArtStage';
import ActionBar from '../../ActionBar';
import SubscribeButton from '../../Button/SubscribeButton';
import SubscribersOnly from '../../ActionBar/_partials/SubscribersOnly';
import Stats from '../../Stats';
import Benefit from '../../DeviationViews/TierProductCardDumb/Benefit';
import JournalTags from '../../DeviationViews/Details/_partials/JournalTags';
import Sidebar from '../../Sidebar';
import { getTierDescription } from '../../DeviationViews/_helpers';
import { isExtendedDeviationFullyLoaded } from '../redux/helpers';
import Comments from '../_partials/Comments';
import { useScrollToDeviationComments } from '../_partials/Comments/useScrollToDeviationComments';
import TierUpsell from '../_partials/TierUpsell';

import s from './MobileTierPage.scss';

interface Props {
  deviation: PapiDeviation;
}

const MobileTierPage: React.FC<React.PropsWithChildren<Props>> = ({ deviation }) => {
  const { t } = useTranslation();
  const isOwner = useIsCurrentUserDeviationAuthor(deviation);
  const isExtendedLoaded = isExtendedDeviationFullyLoaded(deviation);
  const scrollToDeviationComments = useScrollToDeviationComments();
  const {
    extended: { tags } = {} as PapiExtendedDeviation,
    tier: { benefits = [], discord } = {} as PapiDeviationTier,
  } = deviation;
  const hasDiscord = discord?.connected;

  return (
    <div className={s['root']}>
      <ArtStage className={s['stage-content']} deviation={deviation} />
      <ActionBar deviation={deviation} />

      <div className={s['details']}>
        {!isOwner && (
          <BiLoggerContextProvider value={{ referral_info: ReferralType.TIER_UNIQUE }}>
            <SubscribeButton
              className={s['subscribe-button']}
              tier={deviation}
              withSaleIndicator
              withPrice
            />
          </BiLoggerContextProvider>
        )}

        <div className={s['subscribers-and-stats']}>
          <SubscribersOnly deviation={deviation} />
          <Stats
            deviation={deviation}
            withSubscriberCount
            withViewCount={false}
            withBadges={false}
            onCommentClick={scrollToDeviationComments}
          />
        </div>

        {isExtendedLoaded ? (
          <>
            {tags && <JournalTags tags={tags} />}

            <p className={s['description']}>{getTierDescription(deviation)}</p>

            <ul className={s['benefits']}>
              {hasDiscord && (
                <Benefit
                  benefit={t('deviationview.tierCard.benefits.discord')}
                  isDiscord
                  className={s['benefit']}
                />
              )}
              {benefits.map(benefit => (
                <Benefit key={benefit} benefit={benefit} className={s['benefit']} />
              ))}
            </ul>

            <Comments deviation={deviation} className={s['comments']} />
            <div className={s['sidebar']}>
              <Sidebar deviation={deviation} />
            </div>
          </>
        ) : (
          <LoadingIndicator className={s['lloader']} view="llama" />
        )}
      </div>

      <TierUpsell className={s['upsell']} deviation={deviation} />
    </div>
  );
};
MobileTierPage.displayName = 'MobileTierPage';

export default MobileTierPage;
